/**
 * Mock Audio Data Generator
 * Generates deterministic frequency data for thumbnail generation
 * Ensures consistent thumbnails across regenerations
 */

import { AudioFeatureExtractor } from './audio-features.js';

// Singleton extractor for mock data
let mockExtractor = null;

function getMockExtractor() {
  if (!mockExtractor) {
    mockExtractor = new AudioFeatureExtractor({
      sampleRate: 44100,
      fftSize: 2048
    });
  }
  return mockExtractor;
}

/**
 * Reset the singleton mock extractor's temporal state (previousSpectrum,
 * onset history). Must be called before any frame-replay sequence so that
 * accumulated state from a prior generation does not leak in.
 */
export function resetMockFeatureState() {
  if (mockExtractor) {
    mockExtractor.reset();
  }
}

/**
 * Generate mock audio features from frequency data
 * @param {Uint8Array} frequencyData - Mock frequency data
 * @returns {Object} Audio features object
 */
export function generateMockFeatures(frequencyData) {
  const extractor = getMockExtractor();
  return extractor.extract(frequencyData);
}

/**
 * Generate mock frequency data
 * @param {number} intensity - 0.0 to 1.0 representing overall "loudness"
 * @param {number} seed - Random seed for variation (default: use intensity)
 * @returns {Uint8Array} - Frequency data array (1024 bins, values 0-255)
 */
export function generateMockFrequencyData(intensity = 0.5, seed = null) {
  const size = 1024;
  const data = new Uint8Array(size);

  // Use intensity as default seed for consistency
  let s = seed !== null ? seed : Math.floor(intensity * 1000);

  // Simple seeded pseudo-random (mulberry32)
  const random = () => {
    s |= 0;
    s = s + 0x6D2B79F5 | 0;
    let t = Math.imul(s ^ s >>> 15, 1 | s);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };

  for (let i = 0; i < size; i++) {
    // Frequency distribution: bass heavy, treble light (realistic audio spectrum)
    const normalizedFreq = i / size;
    const freqFactor = 1 - normalizedFreq * 0.7;

    // Base amplitude with variation
    const base = intensity * freqFactor * 255;
    const variation = (random() - 0.5) * 50 * intensity;

    // Add some "peaks" at musical frequency bands for visual interest
    // Simulates bass hits, mids, and some highs
    let peakBoost = 0;
    if (i < 32) {
      // Sub-bass / bass region - strong peaks
      peakBoost = (i % 8 < 4) ? 30 * intensity : 0;
    } else if (i < 128) {
      // Low-mid region - moderate peaks
      peakBoost = (i % 16 < 6) ? 20 * intensity : 0;
    } else if (i < 512) {
      // Mid region - subtle peaks
      peakBoost = (i % 32 < 8) ? 15 * intensity : 0;
    }

    data[i] = Math.max(0, Math.min(255, Math.floor(base + variation + peakBoost)));
  }

  return data;
}

/**
 * Generate frequency data for animation (varies with time)
 * Creates a pulsing effect that looks natural
 * @param {number} time - Time in seconds
 * @returns {Uint8Array}
 */
export function generateAnimatedFrequencyData(time) {
  // Create pulsing effect at ~120 BPM (2 beats per second)
  const beatPhase = time * 2 * Math.PI;
  const mainPulse = 0.4 + 0.35 * Math.sin(beatPhase);

  // Add some variation at different rates for interest
  const secondaryPulse = 0.05 * Math.sin(time * Math.PI * 1.5);
  const highFreqWobble = 0.05 * Math.sin(time * Math.PI * 4);

  const intensity = Math.max(0.1, Math.min(0.95, mainPulse + secondaryPulse + highFreqWobble));

  // Use time-based seed for consistent animation across frames
  // but different between time points
  return generateMockFrequencyData(intensity, Math.floor(time * 30));
}
